import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { VideoIcon, PlayCircleIcon, ExternalLinkIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
interface Interview {
  youtubeId: string;
  imageUrl: string;
  titleEn: string;
  titleEs: string;
}
const INTERVIEWS: Interview[] = [{
  youtubeId: '8Qxbu85SdpY',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/3JxzHefhsGANrTjTKBUkgW/Screenshot_2026-04-19_at_11.09.44_PM.png',
  titleEn: 'HAA Interview — Live Conversation',
  titleEs: 'Entrevista HAA — Conversación en Vivo'
}];
export function InterviewsBanner() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  return <section className="relative bg-haa-navy py-16 lg:py-20 overflow-hidden" aria-label={isEs ? 'Entrevistas' : 'Interviews'} data-id="element-2537">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: 'radial-gradient(circle at 25% 50%, white 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }} data-id="element-2538" />

      <div ref={ref} className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2539">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 25
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-10" data-id="element-2540">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-2 mb-5 backdrop-blur-sm" data-id="element-2541">
            <VideoIcon className="w-4 h-4 text-haa-skyblue" data-id="element-2542" />
            <span className="font-nunito text-white/80 text-xs font-bold uppercase tracking-[0.2em]" data-id="element-2543">
              {isEs ? 'En los Medios' : 'In the Media'}
            </span>
          </div>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl text-white tracking-wider leading-none" data-id="element-2544">
            {isEs ? 'Entrevistas y Conversaciones' : 'Interviews & Conversations'}
          </h2>
          <div className="flex items-center justify-center gap-3 mt-4" data-id="element-2545">
            <div className="w-10 h-0.5 bg-haa-skyblue/60 rounded-full" data-id="element-2546" />
            <div className="w-2 h-2 rounded-full bg-haa-skyblue" data-id="element-2547" />
            <div className="w-10 h-0.5 bg-haa-midblue/60 rounded-full" data-id="element-2548" />
          </div>
          <p className="font-nunito text-white/50 text-base mt-5 max-w-xl mx-auto" data-id="element-2549">
            {isEs ? 'Conversaciones con líderes, medios y aliados que destacan la misión y el impacto de la Asociación Hondureño-Americana.' : 'Conversations with leaders, media, and partners spotlighting the mission and impact of the Honduran-American Association.'}
          </p>
        </motion.div>

        {/* Video Grid */}
        <div className={`grid gap-6 ${INTERVIEWS.length === 1 ? 'grid-cols-1 max-w-3xl mx-auto' : 'grid-cols-1 md:grid-cols-2'}`} data-id="element-2550">
          {INTERVIEWS.map((interview, i) => {
          const watchUrl = 'https://www.youtube.com/live/8Qxbu85SdpY?si=i21fX88cv1y7ujKi&t=730';
          return <motion.div key={interview.youtubeId} initial={{
            opacity: 0,
            y: 30
          }} animate={inView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.15 + i * 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className="rounded-2xl overflow-hidden shadow-2xl shadow-black/40 bg-black/40 border border-white/10 group" data-id="element-2551">
                {/* Clickable thumbnail linking to YouTube */}
                <a href={watchUrl} target="_blank" rel="noopener noreferrer" className="relative block w-full overflow-hidden" aria-label={isEs ? 'Ver en YouTube' : 'Watch on YouTube'} data-id="element-2552">
                  <img src={interview.imageUrl} alt={isEs ? interview.titleEs : interview.titleEn} className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-2553" />
                  {/* Play overlay */}
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors duration-300 flex flex-col items-center justify-center gap-3" data-id="element-2554">
                    <PlayCircleIcon className="w-16 h-16 sm:w-20 sm:h-20 text-white/90 drop-shadow-lg group-hover:scale-110 transition-transform duration-300" data-id="element-2555" />
                    <span className="font-nunito text-white text-sm font-bold flex items-center gap-1.5 bg-red-600 px-4 py-2 rounded-full shadow-lg group-hover:bg-red-700 transition-colors" data-id="element-2556">
                      {isEs ? 'Ver en YouTube' : 'Watch on YouTube'}
                      <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-2557" />
                    </span>
                  </div>
                </a>

                {/* Label */}
                <div className="px-5 py-4 bg-gradient-to-r from-haa-navy to-blue-950" data-id="element-2558">
                  <div className="flex items-center justify-between" data-id="element-2559">
                    <p className="font-nunito text-white/90 text-sm font-semibold" data-id="element-2560">
                      {isEs ? interview.titleEs : interview.titleEn}
                    </p>
                    <a href={watchUrl} target="_blank" rel="noopener noreferrer" className="font-nunito text-haa-skyblue hover:text-white text-xs font-bold flex items-center gap-1 transition-colors flex-shrink-0 ml-3" data-id="element-2561">
                      YouTube <ExternalLinkIcon className="w-3 h-3" data-id="element-2562" />
                    </a>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}